import type { ReactNode } from "react";

type CircularProgressProps = {
  /** Progress value from 0 to 1 */
  progress: number;
  /** Outer diameter in pixels. Default: 32 */
  size?: number;
  /** Ring thickness in pixels. Default: 2.5 */
  strokeWidth?: number;
  /** Active ring color. Default: "#43DDA7" */
  color?: string;
  /** Background track color. Default: "rgba(255,255,255,0.15)" */
  trackColor?: string;
  /** When true, shows a subtle pulse on the track ring to indicate buffering */
  loading?: boolean;
  /** Content rendered centered inside the ring (e.g., an icon) */
  children?: ReactNode;
  /** Optional className on the root container */
  className?: string;
};

export default function CircularProgress({
  progress,
  size = 32,
  strokeWidth = 2.5,
  color = "#43DDA7",
  trackColor = "rgba(255,255,255,0.15)",
  loading = false,
  children,
  className,
}: CircularProgressProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.min(Math.max(progress, 0), 1));
  const center = size / 2;

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: size,
        height: size,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="progressbar"
        aria-valuenow={Math.round(progress * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {loading && (
          <style>{`@keyframes cp-pulse{0%,100%{opacity:.15}50%{opacity:.4}}`}</style>
        )}
        {/* Background track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={loading ? color : trackColor}
          strokeWidth={strokeWidth}
          style={
            loading
              ? { animation: "cp-pulse 1.2s ease-in-out infinite" }
              : undefined
          }
        />
        {/* Progress ring */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${center} ${center})`}
          style={{ willChange: "stroke-dashoffset" }}
        />
      </svg>
      {children && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
